import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { ToolbarService } from '../../../core/services/toolbar.service';
import { PromotionCouponsService } from '../../../promotion-coupons/services/promotion-coupons.service';
import { PaymentForm } from '../../forms/payment.form';
import { PaymentStatus } from '../../models/payment-status.model';
import { Payment } from '../../models/payment.model';
import { RedirectCode } from '../../models/redirect-code.enum';
import { PaymentAnalyticsService } from '../../services/payment-analytics.service';
import { PaymentService } from '../../services/payment.service';
import { CouponInfoSnackBarService } from '../../services/coupon-info-snackbar.service';
import { CreditCard } from '../../../credit-card/models/model';
import { LiabilitiesPaymentType } from '../../../liabilities/models/liabilities-payment-type.enum';
import { HandsetService } from '../../../core/services/handset.service';
import {
  PaymentMethodSummaryComponent,
} from '../../../shared/components/payment-method-summary/payment-method-summary.component';
import { PaymentMethodCode } from '../../models/payment-method-code.enum';
import { AuthStoreService } from '../../../shared/services/auth/auth-store.service';
import { CustomerStoreService } from '../../../core/services/customer-store.service';
import { AddToCartEventSource } from '../../models/add-to-cart-event-source.enum';
import { LoaderButtonType } from '../../../shared/models/loader-button-type.enum';
import { AppConfig } from '../../../core/services/app-config.service';

@Component({
  standalone: false,
  selector: 'app-pay',
  styleUrls: ['./pay.component.scss'],
  templateUrl: './pay.component.html',
})
export class PayComponent implements OnDestroy, AfterViewInit, OnInit {
  public readonly PAY_URL = '/liabilities/pay/';
  public readonly CANCEL_PAYMENT_URL = '/liabilities/all';
  public error: { value: string, url?: string, state?: any } = { value: '' };
  public payment: Payment;
  public status: PaymentStatus;
  public creditCards$: Observable<CreditCard[]>;
  public emptyCartCouponId$: Observable<string>;
  public isPaymentEmpty: boolean;
  private readonly destroyed$: Subject<void> = new Subject<void>();
  protected readonly paymentStatus = PaymentStatus;
  protected readonly PaymentMethodCode = PaymentMethodCode;
  public showCloseDueDateInfo: boolean;
  public form = new PaymentForm(PaymentMethodCode.DEFER);
  public redirectCodes = RedirectCode;
  public loading = false;
  public refreshPayment$ = new BehaviorSubject<boolean>(false);
  private initialShowCloseDueDateInfo: boolean;
  public disabled = false;
  public disabledReason: string = '';

  @ViewChild(PaymentMethodSummaryComponent) public paymentMethodSummary: PaymentMethodSummaryComponent;

  constructor(
    private activatedRoute: ActivatedRoute,
    private paymentService: PaymentService,
    private toolbar: ToolbarService,
    private promotionCouponsService: PromotionCouponsService,
    private paymentAnalyticsService: PaymentAnalyticsService,
    private router: Router,
    private authStoreService: AuthStoreService,
    private customerStoreService: CustomerStoreService,
    private couponInfoSnackBarService: CouponInfoSnackBarService,
    public handsetService: HandsetService,
    private appConfig: AppConfig,
  ) {
    this.toolbar.init(this.activatedRoute);
  }

  ngOnInit() {
    this.error = { value: '' };
    var payment = this.appConfig.config.maintenance.payment;
    if (payment !== undefined && payment !== null && payment.length > 0) {
      this.disabledReason = payment;
      this.disabled = true;
    }

    if (this.authStoreService.userInfo.customer_id) {
      return this.customerStoreService
        .updateLimits(this.authStoreService.userInfo.customer_id)
        .subscribe();
    }

  }

  ngAfterViewInit(): void {
    this.initPaymentData();
  }

  private initPaymentData(): void {
    this.initState();
  }

  private checkCouponInfo(payment: Payment): void {
    if (payment.couponInfo
      && Object.keys(payment.couponInfo).length > 0
      && payment.liabilitiesPaymentType !== LiabilitiesPaymentType.TOP_UP
      && this.couponInfoSnackBarService
    ) {
      this.couponInfoSnackBarService.showCouponInfo(payment);
    }
  }

  private initState(): void {
    this.getPayment$()
      .subscribe((payment: Payment) => {
        this.payment = payment;
        this.isPaymentEmpty = !Boolean(this.payment);
        // this.creditCards$ = this.creditCardService.getCreditCardsAndSetDefaultCard(
        //   this.form,
        //   payment?.liabilitiesPaymentType ?? LiabilitiesPaymentType.STANDARD,
        // );
        if (this.payment) {
          if (this.payment.paymentMethodDetails &&
            this.payment.paymentMethodDetails.paymentMethod !== PaymentMethodCode.DEFER) {
            this.trackPaymentEvent(payment);
          }
        }
        if (this.payment.status !== 'STARTED') {
          this.loading = false;
        }
      });
    this.emptyCartCouponId$ = this.activatedRoute.queryParamMap.pipe(map((params) => params.get('emptyCartCouponId')));
  }

  private trackPaymentEvent(payment: Payment): void {
    if (!payment) {
      return;
    }
    const completedStatuses: PaymentStatus[] = [PaymentStatus.AUTOMATIC_COMPLETED, PaymentStatus.COMPLETED];
    if (completedStatuses.includes(payment.status)) {
      this.paymentAnalyticsService.trackPaymentEvent(payment);
    }
  }

  private getPayment$(): Observable<Payment> {
    return this.refreshPayment$.pipe(
      takeUntil(this.destroyed$),
      switchMap(() => this.activatedRoute.params),
      map((params) => params.id),
      tap((value) => console.log(value)),
      switchMap((paymentId) => this.getPayment(paymentId)),
    );
  }

  public expressPaymentChanged($event: boolean): void {
    this.showCloseDueDateInfo = !$event && this.initialShowCloseDueDateInfo;
  }

  private getPayment(paymentId: string): Observable<Payment> {
    if (paymentId) {
      return this.paymentService.getPayment(paymentId, true).pipe(
        tap((payment) => {
          this.refreshReferralIfNeccessary(payment);
          this.checkCouponInfo(payment);
          this.initialShowCloseDueDateInfo = this.paymentService.showCloseDueDateInfo(payment);
          this.showCloseDueDateInfo = this.initialShowCloseDueDateInfo;
        }),
      );
    } else {
      return of(new Payment());
    }
  }

  private refreshReferralIfNeccessary(payment: Payment): void {
    if (payment.status === PaymentStatus.COMPLETED && payment.couponId) {
      this.promotionCouponsService.refreshCoupons();
    }
  }

  public ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  setError(value: { value: string, url?: string, state?: any }) {
    this.error = value;
  }


  public payAll(unpaidIds: string[]): () => Observable<any> {
    return () => this.paymentService.initPayment(unpaidIds, AddToCartEventSource.DASHBOARD_ADD, '/dashboard');
  }

  public submit(payment: any) {
    this.loading = true;
    this.paymentMethodSummary.next(payment);
  }

  public cancelPayment(paymentId: string): void {
    this.paymentService
      .cancelPayment(paymentId)
      .pipe(
        tap(() => this.payment = null),
        tap(() => this.paymentService.refreshBasePayment()),
      )
      .subscribe(() => {
        this.payment = new Payment();
        this.router.navigateByUrl('/liabilities/pay');
      });
  }

  onGoToDeferment() {
    this.router.navigate(['defer'], { relativeTo: this.activatedRoute });
  }

  get startedDeferment() {
    return this.payment && this.payment.id && this.payment?.status === PaymentStatus.STARTED
      && this.payment?.paymentMethodDetails?.paymentMethod === PaymentMethodCode.DEFER;
  }

  public onSubmitButtonLoadingEvent(value: boolean) {
    this.loading = value;
  }

  protected readonly LoaderButtonType = LoaderButtonType;
}
